import { useMemo, useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { dbui_v1_TransformRequest } from '@antimatterhq/asl-api-ts-v1';
import { useFormikContext } from 'formik';
import { useConfigDataSource } from '@/contexts/ConfigDataSourceContext';
import { DataSourcesDataPreviewDialog } from '@/components/dataSources/dataPreviewDialog/DataSourcesDataPreviewDialog';
import { PreTransformSchemaFormValues } from '@/zod/schemas/dataSource/PreTransform';
import {
  mapPreTransformToTransformRequest,
  setSilverPreTransformFormValues,
} from '@/lib/datasource/';
import { useGetTablePreview } from '@/hooks/dbui/useGetTablePreview';
import { Datasource_TransformStage } from '@/types/api';
import { useGetAndPollTransform } from '@/hooks/components/useGetAndPollTransform';
import { useGetFullyQualifiedTableName } from '@/hooks/components/useGetFullyQualifiedTableName';
import { prettifyTransformErrorMessage } from '@/utils/error';

export interface PreTransformDataPreviewContainerProps {
  disabled?: boolean;
}

export const PreTransformDataPreviewContainer = ({
  disabled,
}: PreTransformDataPreviewContainerProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleDataPreviewModalClose = () => setIsDialogOpen(false);

  const { dataSource: initialDatasource } = useConfigDataSource();

  const { name: bronzeTableName = '' } = useGetFullyQualifiedTableName({
    tableName: initialDatasource.spec.bronze?.bronzeTable || '',
    stage: 'bronze',
  });

  // get the current pre-transform values
  const { values } = useFormikContext<PreTransformSchemaFormValues>();

  // set the autoloader input
  const autoloaderInput = useMemo(() => {
    return {
      location: initialDatasource.spec.autoloader?.location || '',
      ...initialDatasource.spec.autoloader,
    };
  }, [initialDatasource]);

  // if we are not using autoloader, call the preview table for the "source table"
  const {
    data: bronzeTablePreview,
    isFetching: getTablePreviewIsFetching,
    error: bronzeTablePreviewError,
  } = useGetTablePreview({
    name: bronzeTableName,
    disabled: !!autoloaderInput.location,
  });

  // handle the transform operation
  const transformRequest = useMemo(() => {
    // preset recreating this request object with every form change
    if (!isDialogOpen) {
      return {
        autoloaderInput: undefined,
        input: undefined,
        transforms: [],
        usePreset: initialDatasource.spec.usePreset,
      };
    }

    const datasource = setSilverPreTransformFormValues(
      initialDatasource,
      values,
    );
    const { autoloader } = datasource.spec;

    const request: Omit<dbui_v1_TransformRequest, 'warehouse'> = {
      autoloaderInput: autoloader?.location ? autoloader : undefined,
      input: bronzeTablePreview ? bronzeTablePreview : undefined,
      transforms: [mapPreTransformToTransformRequest(datasource)],
      usePreset: initialDatasource.spec.usePreset,
    };

    return request;
  }, [bronzeTablePreview, initialDatasource, values, isDialogOpen]);

  const {
    stages,
    isLoading: previewTableIsLoading,
    error: previewTableResponseError,
  } = useGetAndPollTransform(transformRequest, isDialogOpen);

  const sourceTable = useMemo(
    () =>
      stages?.find((stage) => stage.transformType === 'Input') ||
      (bronzeTablePreview as Datasource_TransformStage),
    [stages, bronzeTablePreview],
  );

  const previewTable = useMemo(
    () => stages?.find((stage) => stage.transformType === 'SilverPreTransform'),
    [stages],
  );

  const sourceTableIsLoading = useMemo(() => {
    if (!autoloaderInput.location) {
      return getTablePreviewIsFetching;
    }

    return previewTableIsLoading;
  }, [
    getTablePreviewIsFetching,
    previewTableIsLoading,
    autoloaderInput.location,
  ]);

  const sourceTableResponseError = useMemo(() => {
    if (!autoloaderInput.location) {
      return prettifyTransformErrorMessage(bronzeTablePreviewError);
    }

    return previewTableResponseError;
  }, [
    bronzeTablePreviewError,
    previewTableResponseError,
    autoloaderInput.location,
  ]);

  return (
    <>
      <Button
        onClick={() => setIsDialogOpen(true)}
        variant="primary"
        disabled={disabled || values.method !== 'configure'}
        leftIcon="radix-icons:external-link"
      >
        Open preview
      </Button>
      <DataSourcesDataPreviewDialog
        sourceTable={sourceTable}
        previewTable={previewTable}
        sourceTableStage="Bronze"
        previewTableStage="SilverPreTransform"
        isOpen={isDialogOpen}
        onClose={handleDataPreviewModalClose}
        sourceTableIsLoading={sourceTableIsLoading}
        previewTableIsLoading={previewTableIsLoading}
        sourceTableResponseError={sourceTableResponseError}
        previewTableResponseError={previewTableResponseError}
      />
    </>
  );
};
